import React, { useRef, useState, useCallback, useEffect } from 'react';
import ReactDom from 'react-dom';
import { Loading, Button } from 'carbon-components-react';
import { loadModules } from 'esri-loader';
import PropTypes from 'prop-types';
import { useComponentMount } from 'utilities/hooks.util';
import { isBrowser } from 'react-device-detect';
import Settings from '../Map/settings';
import Terraformer from 'terraformer-arcgis-parser';
import { Add16, Subtract16, SendAlt16, Home16 } from '@carbon/icons-react';
import {
  registerMapInstance,
  unregisterMapInstance,
  addLayerToMap
} from 'utilities/map-instance.util';

const zoomToCenter = (center: number[], mapObj: any) => {
  const { loadedView } = mapObj;
  if (loadedView && !loadedView.destroyed) {
    loadedView
      .goTo({ center, zoom: Settings.zoomLevel })
      .catch((error: any) => {
        if (error.name !== 'AbortError') {
          console.warn('Error zooming to center:', error);
        }
      });
  }
};

const zoomToHome = (mapObj: any) => {
  const { loadedView, originalViewState } = mapObj;
  if (loadedView && originalViewState && !loadedView.destroyed) {
    loadedView
      .goTo({
        center: originalViewState.center,
        zoom: originalViewState.zoom
      })
      .catch((error: any) => {
        if (error.name !== 'AbortError') {
          console.warn('Error zooming to home:', error);
        }
      });
  }
};

const CurrentLocButton: React.FC<{ mapObj: any }> = ({ mapObj }) => {
  const onClickCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition((position) => {
      const center = [position.coords.longitude, position.coords.latitude];
      zoomToCenter(center, mapObj);
    });
  };

  return (
    <Button
      kind="tertiary"
      size="small"
      renderIcon={SendAlt16}
      iconDescription="My current location"
      tooltipPosition="right"
      hasIconOnly
      className="map--current-loc-button"
      onClick={onClickCurrentLocation}
    />
  );
};

const HomeButton: React.FC<{ mapObj: any }> = ({ mapObj }) => {
  const onClickHome = () => {
    zoomToHome(mapObj);
  };

  return (
    <Button
      kind="tertiary"
      size="small"
      renderIcon={Home16}
      iconDescription="Home"
      tooltipPosition="right"
      hasIconOnly
      className="map--home-button"
      onClick={onClickHome}
    />
  );
};

const WebMapViewer: React.FC<Props> = (props) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapObjRef = useRef<any>({});
  const [isLoaded, setIsLoaded] = useState(false);

  const {
    portalItem,
    extent,
    onReady,
    onSelect,
    maxZoomLevel,
    enquiryShape,
    showCurrentLocationButton = false,
    showHomeButton = false,
    onZoomChange
  } = props;

  const { modules } = Settings;
  const {
    webMap,
    mapView,
    extent: extentModule,
    spatialReference,
    graphic,
    graphicsLayer,
    webMercatorUtils,
    config,
    basemap,
    basemapStyle
  } = modules;

  const defaultModules = [
    webMap,
    mapView,
    extentModule,
    spatialReference,
    graphic,
    graphicsLayer,
    webMercatorUtils,
    config,
    modules.geometryEngine,
    basemap,
    basemapStyle
  ];

  const moduleNamespace = defaultModules.map((m) => m.namespace);
  const moduleKey = defaultModules.map((m) => m.key);

  const setEnquiryShapeToMap = useCallback((shape: any) => {
    if (
      mapObjRef.current.loadedMapModules &&
      mapObjRef.current.loadedMap &&
      mapObjRef.current.loadedView
    ) {
      const {
        Graphic,
        WebMercatorUtils,
        GraphicsLayer
      } = mapObjRef.current.loadedMapModules;

      const currentFeatureLayer = new GraphicsLayer({
        id: 'current-enquiry-layer'
      });

      const geometry: any = Terraformer.convert(shape);
      const webMercatorGeometry = WebMercatorUtils.geographicToWebMercator(
        geometry
      );

      const graphic = new Graphic({
        geometry: {
          ...webMercatorGeometry,
          type: 'polygon'
        },
        symbol: Settings.polygonDarkSymbol
      });

      currentFeatureLayer.add(graphic);
      // Use centralized utility to add layer
      addLayerToMap(currentFeatureLayer, 0);
    }
  }, []);

  useEffect(() => {
    if (isLoaded && enquiryShape) {
      setEnquiryShapeToMap(enquiryShape);
    }
  }, [enquiryShape, isLoaded, setEnquiryShapeToMap]);

  useComponentMount(() => {
    loadModules(moduleNamespace, { css: true })
      .then((loadedModules: any[]) => {
        const mapModules: any = {};
        loadedModules.forEach((m, inx) => {
          mapModules[moduleKey[inx]] = m;
        });

        const { WebMap, MapView, Config } = mapModules;

        // Always set API key - interceptors will replace it with session token for basemap requests
        Config.apiKey = process.env.REACT_APP_ESRI_API_KEY;
        if (!Config.apiKey) {
          throw new Error('ArcGIS API key is not set');
        }
        const webmap = new WebMap({
          portalItem: {
            id: portalItem.id
          }
        });

        const view = new MapView({
          container: mapRef.current,
          map: webmap,
          constraints: {
            maxZoom: maxZoomLevel || 22,
            geometry: new mapModules.Extent(Settings.AUS_Boundary_Constraint),
            minScale: Settings.mapMinScale
          }
        });

        if (extent) {
          view.extent = extent;
        }

        view.on('click', (event: __esri.MapViewClickEvent) => {
          if (typeof onSelect === 'function') {
            view
              .hitTest({ x: event.x, y: event.y })
              .then((response: any) => {
                onSelect(response?.results || []);
              })
              .catch((err: any) =>
                console.error('WebMapViewer hit test error:', err.toString())
              );
          }
        });

        view.ui.padding = 0;

        isBrowser
          ? view.ui.move('zoom', 'bottom-left')
          : view.ui.remove('zoom');

        // Add button containers to the map UI
        if (showHomeButton) {
          const homeButton = document.createElement('div');
          homeButton.className = 'map--home-button-container';
          view.ui.add(homeButton, 'top-left');
        }

        if (showCurrentLocationButton) {
          const currentLocButton = document.createElement('div');
          currentLocButton.className = 'map--current-loc-button-container';
          view.ui.add(currentLocButton, 'top-left');
        }

        view.when(() => {
          if (isBrowser) {
            //add icon into zoom button after map view is ready
            const zoomInButton = view.ui
              .find('zoom')
              ?.domNode.getElementsByClassName('esri-icon-plus')
              ?.item(0);
            const zoomOutButton = view.ui
              .find('zoom')
              ?.domNode.getElementsByClassName('esri-icon-minus')
              ?.item(0);
            zoomInButton && ReactDom.render(<Add16 />, zoomInButton);
            zoomOutButton && ReactDom.render(<Subtract16 />, zoomOutButton);

            // Add zoom level tracking
            view.watch('zoom', (newZoom: number) => {
              if (typeof onZoomChange === 'function') {
                onZoomChange(newZoom);
              }
            });
          }

          if (!isLoaded) {
            // Register map instance using centralized utility
            registerMapInstance(webmap, view, mapModules);

            mapObjRef.current = {
              loadedMap: webmap,
              loadedView: view,
              loadedMapModules: mapModules
            };

            if (!window.mapOriginalExtent && extent) {
              window.mapOriginalExtent = extent;
              mapObjRef.current.mapOriginalExtent = extent;
            }

            // Store the initial view state once the view is fully loaded
            view.when(() => {
              if (!mapObjRef.current.originalViewState) {
                mapObjRef.current.originalViewState = {
                  center: view.center.clone(),
                  zoom: view.zoom,
                  scale: view.scale,
                  extent: view.extent.clone()
                };
              }

              // Capture initial zoom level when view is ready
              if (typeof onZoomChange === 'function') {
                onZoomChange(view.zoom);
              }
            });

            typeof onReady === 'function' && onReady();
          }

          // Render button components AFTER mapObjRef is populated
          if (showCurrentLocationButton) {
            const CLBContainer = mapRef.current?.querySelector(
              '.map--current-loc-button-container'
            );
            CLBContainer &&
              ReactDom.render(
                <CurrentLocButton mapObj={mapObjRef.current} />,
                CLBContainer
              );
          }

          if (showHomeButton) {
            const HBContainer = mapRef.current?.querySelector(
              '.map--home-button-container'
            );
            HBContainer &&
              ReactDom.render(
                <HomeButton mapObj={mapObjRef.current} />,
                HBContainer
              );
          }

          setIsLoaded(true);
        });
      })
      .catch((error) => {
        console.error('Failed to load WebMap modules:', error);
      });

    return () => {
      if (window.loadedView) {
        window.mapOriginalExtent = window.loadedView.extent;
        unregisterMapInstance();
        delete window.loadedMap;
        delete window.loadedView;
        delete window.loadedMapModules;
      }
      mapObjRef.current = {};
    };
  });

  return (
    <div className={`map-container ${isLoaded ? '--loaded' : ''}`}>
      <div className="map" ref={mapRef} data-testid="webmap-viewer" />
      {!isLoaded && (
        <div className="map-loader">
          <Loading description="Loading WebMap..." withOverlay={false} />
        </div>
      )}
    </div>
  );
};

const propTypes = {
  portalItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
    portal: PropTypes.shape({
      url: PropTypes.string
    })
  }).isRequired,
  extent: PropTypes.object,
  onReady: PropTypes.func,
  onSelect: PropTypes.func,
  maxZoomLevel: PropTypes.number,
  enquiryShape: PropTypes.object,
  baseMapTheme: PropTypes.string,
  showCurrentLocationButton: PropTypes.bool,
  showHomeButton: PropTypes.bool,
  onZoomChange: PropTypes.func
};

type Props = PropTypes.InferProps<typeof propTypes>;

WebMapViewer.propTypes = propTypes;

WebMapViewer.defaultProps = {
  maxZoomLevel: 22,
  baseMapTheme: 'light',
  showCurrentLocationButton: false,
  showHomeButton: false
};

export default React.memo(WebMapViewer);
